import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { baseURL } from "../backend_url";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  MenuItem,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  Tab,
  Tabs,
  IconButton,
  Divider,
  Chip,
  Tooltip,
  InputAdornment,
  Stack,
  Switch,
  Backdrop,
  LinearProgress,
} from "@mui/material";
import {
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterListIcon,
  Layers as LayersIcon,
  GridView as GridViewIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { motion, AnimatePresence } from "framer-motion";


// --- Constants ----------------------------------------------------------------
const lengthOptionsList = ["1", "2", "3", "4", "5", "6", "Weekly", "Monthly"];

// Optional: show logos next to supplier names (add/extend as needed)
const supplierLogos = {
  "Aucomm Car Rental": "https://logo.clearbit.com/aucommcarrental.com",
  Finalrentals: "https://logo.clearbit.com/finalrentals.com",
  "ABBYCAR": "https://logo.clearbit.com/abbycar.com",
  "ACE Rent a car": "https://logo.clearbit.com/acerentacar.com",
  "AZ Car Rental": "https://logo.clearbit.com/azcarrental.com",
  "Americars Auto Rental": "https://logo.clearbit.com/americarsautorental.com",
  Autounion: "https://logo.clearbit.com/autounion.gr",
  "Blue Auto Trading": "https://logo.clearbit.com/blueautotrading.com",
  EXER: "https://logo.clearbit.com/exer.gr",
  Easirent: "https://logo.clearbit.com/easirent.com",
  FOX: "https://logo.clearbit.com/foxrentacar.com",
  Flexways: "https://logo.clearbit.com/flexways.com",
  "H-Lead Hospitality": "https://logo.clearbit.com/h-leadhospitality.com",
  "MEX RENT A CAR": "https://logo.clearbit.com/mexrentacar.com",
  "MOVIS Car Rental": "https://logo.clearbit.com/movis.com",
  Optimo: "https://logo.clearbit.com/optimorent.com",
  Priceless: "https://logo.clearbit.com/pricelesscarrental.com",
  "Rent a Car 4Less": "https://logo.clearbit.com/rentacar4less.com",
  RightCars: "https://logo.clearbit.com/right-cars.com",
  SIXT: "https://logo.clearbit.com/sixt.com",
  "Seven Auto": "https://logo.clearbit.com/sevenautorent.com",
  "Stride Rent a Car": "https://logo.clearbit.com/striderentacar.com",
  "United Rent A Car": "https://logo.clearbit.com/unitedrentacar.com",
  "Victory Car Rental": "https://logo.clearbit.com/victorycarrental.com",
  WheeGo: "https://logo.clearbit.com/wheego.com",
  "Zoom Rent A Car": "https://logo.clearbit.com/zoomrentals.com",
  yesaway: "https://logo.clearbit.com/yesaway.com",
  Avis: "https://logo.clearbit.com/avis.com",
  Hertz: "https://logo.clearbit.com/hertz.com",
  Budget: "https://logo.clearbit.com/budget.com",
  Europcar: "https://logo.clearbit.com/europcar.com",
  Thrifty: "https://logo.clearbit.com/thrifty.com",
  Sixt: "https://logo.clearbit.com/sixt.com",
  Alamo: "https://logo.clearbit.com/alamo.com",
  National: "https://logo.clearbit.com/nationalcar.com",
  Enterprise: "https://logo.clearbit.com/enterprise.com",
  Dollar: "https://logo.clearbit.com/dollar.com",
  Payless: "https://logo.clearbit.com/paylesscar.com",
  Fox: "https://logo.clearbit.com/foxrentacar.com",
  Discount: "https://logo.clearbit.com/discountcar.com",
  "Routes Car Rental": "https://logo.clearbit.com/routes.ca",
  Economy: "https://logo.clearbit.com/economybookings.com",
  ACE: "https://logo.clearbit.com/acerentacar.com",
  Zoom: "https://logo.clearbit.com/zoomrentals.com",
  Practicar: "https://logo.clearbit.com/practicar.ca",
  Mex: "https://logo.clearbit.com/mexrentacar.com",
  'U-Save': "https://logo.clearbit.com/usave.com",
  GreenMotion: "https://logo.clearbit.com/greenmotion.com",
  NextCar: "https://logo.clearbit.com/nextcarrental.com",
  Silvercar: "https://logo.clearbit.com/silvercar.com",
  HertzLocal: "https://logo.clearbit.com/hertz.com",
  Canadream: "https://logo.clearbit.com/canadream.com",
  Oneway: "https://logo.clearbit.com/onewaycarrental.com",
  Autorent: "https://logo.clearbit.com/autorent.ca",
  Kayak: "https://logo.clearbit.com/kayak.com",
  CarTrawler: "https://logo.clearbit.com/cartrawler.com",
  Globe: "https://logo.clearbit.com/globecar.com",
  Holiday: "https://logo.clearbit.com/holidayautos.com",
  NU: "https://logo.clearbit.com/nucarrentals.com",
  RentAWreck: "https://logo.clearbit.com/rentawreck.ca",
  Woodbridge: "https://logo.clearbit.com/woodbridgeauto.com",
  York: "https://logo.clearbit.com/yorkcarrental.com",
  VipCars: "https://logo.clearbit.com/vipcarrental.com",
  ZoomCar: "https://logo.clearbit.com/zoomcar.com",
  BudgetTruck: "https://logo.clearbit.com/budgettruck.com",
  DrivingForce: "https://logo.clearbit.com/drivingforce.ca",
  Standard: "https://logo.clearbit.com/standardcarrental.com",
  Advantage: "https://logo.clearbit.com/advantage.com",
  Firefly: "https://logo.clearbit.com/fireflycarrental.com",
  Apex: "https://logo.clearbit.com/apexcarrental.com",
};

function PriceRankLegend() {
  const theme = useTheme();
  return (
    <Paper elevation={1} sx={{ mb: 2, px: 2, py: 1, display: 'inline-block', borderLeft: '6px solid', borderColor: theme.palette.divider }}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>Price Color Indicator</Typography>
      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: theme.palette.success.light, border: '1px solid', borderColor: theme.palette.divider }} />
          <Typography variant="caption">Lowest (1st)</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: theme.palette.warning.main, border: '1px solid', borderColor: theme.palette.divider }} />
          <Typography variant="caption">2nd Lowest</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: theme.palette.error.main, border: '1px solid', borderColor: theme.palette.divider }} />
          <Typography variant="caption">3rd Lowest</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}



// --- Helpers -------------------------------------------------------------------
const parsePriceToNumber = (priceStr = "") => {
  const n = parseFloat(String(priceStr).replace(/[^0-9.]/g, ""));
  return isNaN(n) ? null : n;
};

const currencyFromResults = (data) => {
  // Try to infer currency from any price string
  for (const sup of Object.keys(data || {})) {
    const sipps = data[sup] || {};
    for (const sipp of Object.keys(sipps)) {
      const pays = sipps[sipp] || {};
      for (const pay of Object.keys(pays)) {
        const arr = pays[pay] || [];
        if (arr.length) {
          const str = arr[0];
          const match = String(str).match(/[A-Z]{3}|[$€£₹]/);
          if (match) return match[0];
        }
      }
    }
  }
  return "";
};

// Compute Top 3 lows for each SIPP||pay across ALL suppliers
const computeLowestMap = (data) => {
  const all = {};
  Object.entries(data || {}).forEach(([sup, bySipp]) =>
    Object.entries(bySipp || {}).forEach(([sipp, byPay]) =>
      Object.entries(byPay || {}).forEach(([pay, offers]) => {
        const key = `${sipp}||${pay}`;
        (offers || []).forEach((priceStr) => {
          const num = parsePriceToNumber(priceStr);
          if (num !== null) {
            (all[key] ||= []).push(num);
          }
        });
      })
    )
  );
  const map = {};
  Object.entries(all).forEach(([k, arr]) => {
    const uniq = Array.from(new Set(arr)).sort((a, b) => a - b);
    map[k] = uniq.slice(0, 3);
  });
  return map;
};

const computeGrouped = (data) => {
  const grp = {};
  Object.entries(data || {}).forEach(([, bySipp]) =>
    Object.entries(bySipp || {}).forEach(([sipp, byPay]) => {
      grp[sipp] = Object.keys(byPay || {});
    })
  );
  return grp;
};

const getAllPaymentTypes = (data) => {
  const set = new Set();
  Object.values(data || {}).forEach((bySipp) =>
    Object.values(bySipp || {}).forEach((byPay) =>
      Object.keys(byPay || {}).forEach((p) => set.add(p))
    )
  );
  return Array.from(set).sort();
};

const collectAllNumbers = (data) => {
  const arr = [];
  Object.values(data || {}).forEach((bySipp) =>
    Object.values(bySipp || {}).forEach((byPay) =>
      Object.values(byPay || {}).forEach((offers) =>
        (offers || []).forEach((priceStr) => {
          const n = parsePriceToNumber(priceStr);
          if (n !== null) arr.push(n);
        })
      )
    )
  );
  return arr;
};

// Heat value 0..1 based on min/max
const heat = (value, min, max) => {
  if (min == null || max == null || value == null || max === min) return 0;
  return (value - min) / (max - min);
};

// --- Component -----------------------------------------------------------------
export default function EnhancedUpdateForm() {
  const theme = useTheme();

  // Form state (UNCHANGED LOGIC)
  const [formData, setFormData] = useState({
    pickupLocation: "",
    pickupDate: "",
    dropoffDate: "",
    pickupTime: "",
    dropoffTime: "",
    residence: "United States of America",
    currency: "USD",
    lengths: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState({});
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedLength, setSelectedLength] = useState("");

  // NEW: UI controls
  const [supplierQuery, setSupplierQuery] = useState("");
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [denseMode, setDenseMode] = useState(false);

  // Times
  const timeOptions = useMemo(() => {
    const opts = [];
    for (let h = 0; h < 24; h++) {
      const hh = String(h).padStart(2, "0");
      opts.push(`${hh}:00`, `${hh}:30`);
    }
    return opts;
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLengthsChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      lengths: typeof value === "string" ? value.split(",").map((v) => v.toString()) : value.map((v) => v.toString()),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const resp = await axios.post(`${baseURL}/fetchRatesFromDiscoverEnhanced`, formData);
      if (!resp.data.success) throw new Error(resp.data.error || "Search failed");

      const data = resp.data.data || {};
      setResults(data);

      const dts = Object.keys(data);
      setDates(dts);

      if (dts.length) {
        setSelectedDate(dts[0]);
        const available = formData.lengths.filter((l) => data[dts[0]]?.[l]);
        setSelectedLength(available.length ? available[0] : "");
      }
    } catch (err) {
      setError(
        err.response
          ? `Request failed (${err.response.status}): ${err.response.data?.error || err.response.statusText}`
          : err.message
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDate && results[selectedDate]) {
      const avail = formData.lengths.filter((l) => Object.prototype.hasOwnProperty.call(results[selectedDate], l));
      if (!avail.includes(selectedLength)) {
        setSelectedLength(avail.length ? avail[0] : "");
      }
    }
  }, [selectedDate, results, formData.lengths]);

  // --- Derived UI data ---------------------------------------------------------
  const currentData = selectedDate && selectedLength ? results[selectedDate]?.[selectedLength] : {};
  const lowestMap = useMemo(() => computeLowestMap(currentData), [currentData]);
  const grouped = useMemo(() => computeGrouped(currentData), [currentData]);
  const suppliers = useMemo(() => Object.keys(currentData || {}).sort(), [currentData]);
  const allPaymentTypes = useMemo(() => getAllPaymentTypes(currentData), [currentData]);
  const priceNumbers = useMemo(() => collectAllNumbers(currentData), [currentData]);
  const minPrice = useMemo(() => (priceNumbers.length ? Math.min(...priceNumbers) : null), [priceNumbers]);
  const maxPrice = useMemo(() => (priceNumbers.length ? Math.max(...priceNumbers) : null), [priceNumbers]);
  const inferredCurrency = useMemo(() => currencyFromResults(currentData), [currentData]);

  // Filters
  const filteredSuppliers = useMemo(() => {
    const q = supplierQuery.trim().toLowerCase();
    const paySet = new Set(selectedPayments.length ? selectedPayments : allPaymentTypes);
    return suppliers.filter((s) => s.toLowerCase().includes(q)).filter((s) => {
      const bySipp = currentData[s] || {};
      // At least one visible cell must match selected payment filter
      return Object.values(bySipp).some((byPay) => Object.keys(byPay).some((p) => paySet.has(p)));
    });
  }, [supplierQuery, selectedPayments, suppliers, currentData, allPaymentTypes]);

  // Build headers
  const sipps = useMemo(() => Object.keys(grouped), [grouped]);
  const headers = useMemo(() => sipps.flatMap((s) => grouped[s].map((p) => `${s}||${p}`)), [sipps, grouped]);

  // --- UI Renderers ------------------------------------------------------------
  const KPICard = ({ title, value, icon }) => (
    <Paper elevation={3} sx={{ p: 2, height: "100%" }}>
      <Stack direction="row" spacing={2} alignItems="center">
        {icon}
        <Box>
          {/* {priceRankLegend} */}
          <Typography variant="overline" color="text.secondary">{title}</Typography>
          <Typography variant="h6" sx={{ mt: 0.5 }}>{value}</Typography>
        </Box>
      </Stack>
    </Paper>
  );

  const ControlBar = (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            placeholder="Filter suppliers…"
            value={supplierQuery}
            onChange={(e) => setSupplierQuery(e.target.value)}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )}}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl size="small" fullWidth>
            <InputLabel>Payment Types</InputLabel>
            <Select
              multiple
              label="Payment Types"
              value={selectedPayments}
              onChange={(e) => setSelectedPayments(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              renderValue={(vals) => (vals.length ? vals.join(", ") : "All")}
              startAdornment={<InputAdornment position="start"><FilterListIcon /></InputAdornment>}
            >
              {allPaymentTypes.map((p) => (
                <MenuItem key={p} value={p}>
                  <Checkbox checked={selectedPayments.includes(p) || selectedPayments.length === 0} />
                  <ListItemText primary={p} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            <Chip icon={<LayersIcon />} label={sipps.length ? `${sipps.length} SIPP` : "No SIPP"} variant="outlined" />
            <Tooltip title="Dense rows">
              <Stack direction="row" spacing={1} alignItems="center">
                <GridViewIcon fontSize="small" />
                <Switch size="small" checked={denseMode} onChange={(e) => setDenseMode(e.target.checked)} />
              </Stack>
            </Tooltip>
            <Tooltip title="Refresh current view">
              <span>
                <IconButton onClick={handleSubmit} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderTable = (data, title) => {
    if (!data || Object.keys(data).length === 0) return <Typography>No rates found.</Typography>;

    const paySet = new Set(selectedPayments.length ? selectedPayments : allPaymentTypes);

    return (
      <Box key={title} sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>{title}</Typography>

        {/* KPI Row */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <KPICard
              title="Suppliers"
              value={Object.keys(data).length}
              icon={<LayersIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KPICard
              title="Cheapest"
              value={minPrice != null ? `${inferredCurrency || formData.currency} ${minPrice}` : "—"}
              icon={<TrendingDownIcon color="success" />}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KPICard
              title="Most Expensive"
              value={maxPrice != null ? `${inferredCurrency || formData.currency} ${maxPrice}` : "—"}
              icon={<TrendingUpIcon color="error" />}
            />
          </Grid>
        </Grid>
        <PriceRankLegend />

        {ControlBar}

        <TableContainer component={Paper} sx={{ overflow: "auto", borderRadius: 2 }}>
          <Table stickyHeader size={denseMode ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell
                  rowSpan={2}
                  sx={{
                    position: "sticky",
                    left: 0,
                    top: 0,
                    zIndex: 3,
                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.grey[100],
                    borderRight: `0.5px solid ${theme.palette.divider}`,
                    fontWeight: "bold",
                    minWidth: 220,
                  }}
                >
                  Supplier
                </TableCell>
                {sipps.map((s) => (
                  <TableCell
                    key={s}
                    align="center"
                    colSpan={(grouped[s] || []).filter((p) => paySet.has(p)).length}
                    sx={{
                      border: `0.5px solid ${theme.palette.divider}`,
                      bgcolor: theme.palette.secondary.light,
                      fontWeight: "bold",
                      minWidth: 150,
                    }}
                  >
                    {s}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {sipps.flatMap((s) => (grouped[s] || []).filter((p) => paySet.has(p)).map((p) => `${s}||${p}`)).map((h) => (
                  <TableCell
                    key={h}
                    align="center"
                    sx={{
                      border: `0.5px solid ${theme.palette.divider}`,
                      bgcolor: theme.palette.info.light,
                      fontSize: "0.875rem",
                      minWidth: 150,
                    }}
                  >
                    {h.split("||")[1]}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <AnimatePresence initial={false}>
                {filteredSuppliers.map((sup) => (
                  <SupplierRow
                    key={sup}
                    sup={sup}
                    bySipp={data[sup]}
                    headers={sipps.flatMap((s) => (grouped[s] || []).filter((p) => paySet.has(p)).map((p) => `${s}||${p}`))}
                    lowestMap={lowestMap}
                    minPrice={minPrice}
                    maxPrice={maxPrice}
                    theme={theme}
                  />
                ))}
              </AnimatePresence>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // --- Layout ------------------------------------------------------------------
  return (
    <Box sx={{ p: 2, minHeight: "100vh", display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Top: Search Panel (full width) */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Search Criteria</Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Pick-up Location" name="pickupLocation" value={formData.pickupLocation} onChange={handleChange} size="small" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField fullWidth type="date" label="Pick-up Date" name="pickupDate" InputLabelProps={{ shrink: true }} value={formData.pickupDate} onChange={handleChange} size="small" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField fullWidth type="date" label="Drop-off Date" name="dropoffDate" InputLabelProps={{ shrink: true }} value={formData.dropoffDate} onChange={handleChange} size="small" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField select fullWidth label="Pick-up Time" name="pickupTime" value={formData.pickupTime} onChange={handleChange} size="small">
                  {timeOptions.map((t) => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField select fullWidth label="Drop-off Time" name="dropoffTime" value={formData.dropoffTime} onChange={handleChange} size="small">
                  {timeOptions.map((t) => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField select fullWidth label="Origin" name="residence" value={formData.residence} onChange={handleChange} size="small">
                  {["United States of America", "India", "Canada"].map((r) => (<MenuItem key={r} value={r}>{r}</MenuItem>))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField select fullWidth label="Currency" name="currency" value={formData.currency} onChange={handleChange} size="small">
                  {["USD", "INR", "EUR"].map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Length of Rental</InputLabel>
                  <Select multiple name="lengths" value={formData.lengths} onChange={handleLengthsChange} renderValue={(vals) => vals.join(", ")}>
                    {lengthOptionsList.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        <Checkbox checked={formData.lengths.includes(opt)} />
                        <ListItemText primary={opt} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <LoadingButton type="submit" variant="contained" loading={loading} fullWidth>Search Rates</LoadingButton>
              </Grid>
            </Grid>
          </Box>
          {error && (<Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>)}
        </CardContent>
      </Card>

      {/* Results Panel */}
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        {dates.length > 0 && (
          <>
            <Paper elevation={1} sx={{ mb: 1 }}>
              <Tabs value={selectedDate} onChange={(_, d) => setSelectedDate(d)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: "divider" }}>
                {dates.map((date) => (<Tab key={date} label={date} value={date} />))}
              </Tabs>
            </Paper>

            {selectedDate && formData.lengths.some((l) => results[selectedDate]?.[l]) && (
              <Paper elevation={1} sx={{ mb: 2 }}>
                <Tabs value={selectedLength} onChange={(_, l) => setSelectedLength(l)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: "divider" }}>
                  {formData.lengths.filter((l) => results[selectedDate]?.[l]).map((l) => (
                    <Tab key={l} label={`LOR - ${l}`} value={l} />
                  ))}
                </Tabs>
              </Paper>
            )}

            <AnimatePresence mode="wait">
              {selectedLength && results[selectedDate]?.[selectedLength] && (
                <motion.div key={`${selectedDate}-${selectedLength}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                  {renderTable(results[selectedDate][selectedLength], `LOR - ${selectedLength}`)}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {(!dates.length && !loading) && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Start by searching for rates</Typography>
              <Typography variant="body2" color="text.secondary">
                Fill in your criteria above and click <b>Search Rates</b>. Results will appear here with color-coded highlights, supplier logos.
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1}>
                <Chip label="Sticky supplier column" />
                <Chip label="Heatmap cells" />
                <Chip label="Top-3 lowlights" />
                <Chip label="Filter by payment type" />
              </Stack>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Premium Loader Overlay */}
      <Backdrop open={loading} sx={{ zIndex: (t) => t.zIndex.drawer + 1, color: '#fff', flexDirection: 'column', gap: 2 }}>
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center' }}>Please wait, fetching live rates for you…</Typography>
        </motion.div>
        <Box sx={{ width: { xs: '80%', sm: '60%', md: '40%' } }}>
          <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
            <LinearProgress sx={{ borderRadius: 1 }} />
          </motion.div>
        </Box>
        <Typography variant="caption" sx={{ opacity: 0.9 }}>Optimizing routes, normalizing suppliers, and comparing prices…</Typography>
      </Backdrop>
    </Box>
  );

  
}

// --- Supplier Row with expansion and heat background --------------------------
function SupplierRow({ sup, bySipp, headers, lowestMap, minPrice, maxPrice, theme }) {
  const [open, setOpen] = useState(false);

  const supCell = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <img src={supplierLogos[sup]} alt={sup} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} style={{ height: 24 }} />
      <Typography fontWeight={700}>{sup}</Typography>
      <IconButton size="small" onClick={() => setOpen((v) => !v)} sx={{ ml: 'auto' }}>
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    </Box>
  );

  return (
    <>
      <TableRow hover>
        <TableCell
          component="th"
          scope="row"
          sx={{
            position: "sticky",
            left: 0,
            bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.grey[100],
            borderRight: `0.5px solid ${theme.palette.divider}`,
            minWidth: 220,
          }}
        >
          {supCell}
        </TableCell>
        {headers.map((h) => {
          const [sipp, pay] = h.split("||");
          const offers = bySipp?.[sipp]?.[pay] || [];
          const lows = lowestMap[h] || [];

          return (
            <TableCell key={h} align="center" sx={{ border: `0.5px solid ${theme.palette.divider}`, p: 0, minWidth: 150 }}>
              <Stack sx={{ p: 0.5 }}>
                {offers.length === 0 && (
                  <Typography variant="caption" color="text.disabled">—</Typography>
                )}
                {offers.map((priceStr, i) => {
                  const num = parsePriceToNumber(priceStr);
                  const rankColor =
                    num === lows[0]
                      ? theme.palette.success.light
                      : num === lows[1]
                      ? theme.palette.warning.light
                      : num === lows[2]
                      ? theme.palette.error.light
                      : "transparent";

                  const intensity = heat(num, minPrice, maxPrice);
                  const bg = rankColor !== "transparent" ? rankColor : `linear-gradient(135deg, rgba(0,0,0,${0.04 + intensity * 0.06}), transparent)`;

                  return (
                    <Tooltip key={i} title={`SIPP: ${sipp} | Pay: ${pay} | Price: ${priceStr}`} arrow>
                      <Box
                        sx={{
                          background: bg,
                          m: 0.25,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          border: `0.5px solid ${theme.palette.divider}`,
                          fontWeight: num === lows[0] ? 700 : 400,
                          fontSize: "0.875rem",
                          transition: "transform 0.15s ease, box-shadow 0.15s ease",
                          '&:hover': { transform: 'translateY(-1px)', boxShadow: 1 },
                        }}
                      >
                        {priceStr}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Stack>
            </TableCell>
          );
        })}
      </TableRow>

      {/* Expandable: show structured JSON for this supplier (debug/insight) */}
      <TableRow>
        <TableCell colSpan={1 + headers.length} sx={{ p: 0, border: 0 }}>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                <Divider />
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Offers breakdown for {sup}
                  </Typography>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(bySipp, null, 2)}</pre>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </TableCell>
      </TableRow>
    </>
  );
}
